<template>
    <dialog
        ref="dialog"
        class="live-search-dialog"
        aria-labelledby="quote-live-search-title"
        @close="stop"
        @cancel="stop"
        @click="closeOnBackdrop"
    >
        <div class="modal-header">
            <h5 id="quote-live-search-title" class="modal-title">Live search · {{ selectedId }}</h5>
            <button
                type="button"
                class="btn-close"
                aria-label="Close live-search status"
                @click="close"
            />
        </div>
        <div class="modal-body">
            <LiveSearchStatusContent
                v-if="loading || failed || runs.length"
                :runs="runs"
                :loading="loading"
                :resume-failed="failed"
            />
            <p v-if="!loading && !failed && !runs.length" class="mb-0">
                No saved live-search status is available for this search.
            </p>
            <p v-if="failed" class="mb-2">
                We couldn't refresh the status. The search may still be running.
            </p>
            <button
                v-if="!loading && (failed || !runs.length || runs.some((run) => run.stopped))"
                type="button"
                class="btn btn-outline-secondary btn-sm"
                @click="retry"
            >
                {{ failed ? 'Try again' : 'Refresh status' }}
            </button>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="close">Close</button>
        </div>
    </dialog>
</template>

<script>
    import SearchService from '@/services/search.service'
    import LiveSearchStatusContent from '@/components/shared/LiveSearchStatusContent.vue'

    export default {
        name: 'LiveSearchQuoteStatusDialog',
        components: { LiveSearchStatusContent },
        data() {
            return {
                selectedId: null,
                runs: [],
                loading: false,
                failed: false,
                controller: null,
                timer: null,
            }
        },
        beforeUnmount() {
            this.stop()
        },
        methods: {
            open(row) {
                this.stop()
                this.selectedId = row.id
                this.runs = []
                this.failed = false
                this.loading = true
                this.controller = new AbortController()
                this.$refs.dialog.showModal()
                this.refresh(this.controller)
            },
            stop() {
                clearTimeout(this.timer)
                this.controller?.abort()
                this.controller = null
            },
            close() {
                this.stop()
                this.$refs.dialog.close()
            },
            retry() {
                this.stop()
                this.failed = false
                this.loading = true
                this.controller = new AbortController()
                this.refresh(this.controller)
            },
            async refresh(controller) {
                try {
                    const response = await SearchService.fetchLiveSearchSummary(
                        this.selectedId,
                        controller.signal
                    )
                    if (controller.signal.aborted) return
                    if (
                        !Array.isArray(response?.runs) ||
                        response.runs.some(
                            (run) =>
                                !['running', 'complete', 'unknown'].includes(run.status) ||
                                !Array.isArray(run.vendors)
                        )
                    )
                        throw new Error('Invalid live-search status')
                    this.runs = response.runs.map((run) => ({
                        ...run,
                        complete: run.status === 'complete',
                        stopped: run.status === 'unknown',
                        delivery: 'polling',
                    }))
                    this.failed = false
                    if (response.runs.some((run) => run.status === 'running')) {
                        this.timer = setTimeout(() => this.refresh(controller), 10000)
                    }
                } catch {
                    if (controller.signal.aborted) return
                    this.failed = true
                    this.runs = this.runs.map((run) =>
                        run.complete ? run : { ...run, stopped: true }
                    )
                } finally {
                    if (!controller.signal.aborted) this.loading = false
                }
            },
            closeOnBackdrop(event) {
                if (event.target === this.$refs.dialog) this.close()
            },
        },
    }
</script>

<style scoped>
    .live-search-dialog {
        position: fixed;
        inset: 0;
        margin: auto;
        width: min(480px, calc(100vw - 32px));
        max-height: 85vh;
        overflow: auto;
        padding: 0;
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: 12px;
        color: var(--bs-body-color, #212529);
        background: var(--bs-body-bg, #fff);
    }
    .live-search-dialog::backdrop {
        background: rgb(0 0 0 / 45%);
    }
    .modal-header,
    .modal-body,
    .modal-footer {
        padding: 16px 24px;
    }
    .modal-header {
        gap: 12px;
    }
    .modal-header .btn-close {
        margin-left: auto;
        flex-shrink: 0;
    }
</style>
